import { ensureDefined } from "common/utils/assertOfType";
import { VALIDATION_ENDPOINT, logout } from "lib/auth";
import BuilderName from "lib/components/builders/BuilderName";
import SelectBuilderModal from "lib/components/modals/SelectBuilderModal";
import IconRole from "lib/components/typedIcons/IconRole";
import Avatar from "lib/components/users/Avatar";
import {
  accountSwitcherPath,
  adminPath,
  builderAccountPath,
  builderHomePagePath,
  studioPath,
  useAppRouterParams,
} from "lib/routeHelpers";
import { userCanEditStudioFiles } from "lib/security/client";
import { userIsSuperAdmin } from "lib/security/shared";
import { useUserContext } from "lib/state/UserContextProvider";
import { putUser } from "lib/storage/userStorage";
import getUserFullName from "lib/utils/userName";
import { type ReactNode, useRef, useState } from "react";
import { useSWRConfig } from "swr";
import DropdownMenu from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import PopoverMenu from "ui/components/PopoverMenu";
import cx from "ui/cx";
import useAppContext from "ui/hooks/useAppContext";
import IconCheck from "ui/icons/IconCheck";
import IconDashboard from "ui/icons/IconDashboard";
import IconFileOpen from "ui/icons/IconFileOpen";
import IconSalesConfig from "ui/icons/IconSalesConfig";
import IconSettings12 from "ui/icons/IconSettings12";
import IconSignOut from "ui/icons/IconSignOut";
import IconUpArrow from "ui/icons/IconUpArrow";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./UserMenu.module.scss";

interface UserMenuProps {
  showStudioLink?: boolean;
  inAdminDashboard?: boolean;
  selectBuilder?: (builderId: string) => void;
  className?: string;
}

export default function UserMenu({ showStudioLink, selectBuilder, inAdminDashboard, className }: UserMenuProps) {
  const { builderId } = useAppRouterParams();

  const opener = useRef<HTMLDivElement | null>(null);

  const [openMenu, setOpenMenu] = useState(false);
  const [showSelectBuilderModal, setShowSelectBuilderModal] = useState(false);

  const { hasPermission, user, builderUser, builderUserRoleName, builderPoliciesAndRoles, builder, isSuperAdmin } =
    useUserContext();
  const { setUserNotification } = useAppContext();
  const builderRoles = builderPoliciesAndRoles?.roles.map((r) => r.name) ?? [];
  const iconName = builderUserRoleName === "super_admin" ? "super_admin" : "sales_manager";

  const showBuilderInfo = !!builderUser || isSuperAdmin;
  const userName = getUserFullName(user);
  const { mutate: updateUser } = useSWRConfig();

  const switchUserRole = async (userRole?: string): Promise<void> => {
    try {
      // Since super_admin is not an actual BuilderUserRole, setting the tempUserRole property to null
      // restores your super_admin status instead.
      const tempUserRole = userRole !== "super_admin" ? userRole : null;
      await putUser(ensureDefined(user?.id), { tempUserRole });

      updateUser(VALIDATION_ENDPOINT);

      if (tempUserRole) {
        setUserNotification({
          messageType: "info",
          message: `Switched role to ${tempUserRole}. User-level permission overrides will be ignored while impersonating another role.`,
        });
      }
    } catch (e) {
      setUserNotification({ messageType: "error", message: "Error attempting to switch roles." });
    }
  };

  const isUserRole = (role: string): boolean => {
    if (!builderUserRoleName && role === "super_admin") {
      return true;
    }
    return builderUserRoleName === role;
  };

  const getIconDisplaySize = (role: string): number => (isUserRole(role) ? 10 : 0);

  const roleOptions: ReactNode[] = ["super_admin", ...builderRoles].map((role) => {
    const className = `${role} ${isUserRole(role) ? "selected" : ""}`;
    return (
      <Link key={role} className={className} onClick={() => switchUserRole(role)}>
        <IconRole userRole={iconName} />
        {role === "super_admin" ? "Super Admin" : role}
        <IconCheck color="blueDarkest" size={getIconDisplaySize(role)} />
      </Link>
    );
  });

  const builderLinksVisible = !inAdminDashboard && !!builderId;
  const studioLinkVisible = showStudioLink && userCanEditStudioFiles({ hasPermission, user, builder });
  const roleImpersonationEnabled = userIsSuperAdmin(user, true) && builderLinksVisible;

  return (
    <>
      <div
        ref={opener}
        onClick={() => setOpenMenu(!openMenu)}
        className={cx(styles.wrapper, !!builderId && styles.showBuilderInfo, className)}
      >
        {!inAdminDashboard && <BuilderName />}
        {!roleImpersonationEnabled && <Avatar user={user} avatarSize="small" />}
        {roleImpersonationEnabled && (
          <Tooltip.Root delayDuration={0}>
            <Tooltip.Trigger asChild>
              <div className={`avatar_wrapper avatar_${iconName ?? "super_admin"}`}>
                <Avatar user={user} avatarSize="small" className="avatar" />
              </div>
            </Tooltip.Trigger>
            {builderUserRoleName && (
              <Tooltip.Content side="left">
                Impersonating Role:&nbsp;
                {builderUserRoleName}
              </Tooltip.Content>
            )}
          </Tooltip.Root>
        )}
        {openMenu && (
          <>
            <DropdownMenu opener={opener} onClose={() => setOpenMenu(false)}>
              <>
                <div className="dropdown-menu-header">
                  <div className="dropdown-menu-header-title">
                    {showBuilderInfo && !inAdminDashboard ? builder?.companyName : userName}
                  </div>
                  <div className="dropdown-menu-header-subtitle">
                    <Tooltip.Root delayDuration={0}>
                      <Tooltip.Trigger asChild>
                        <div className={`${iconName}_icon`}>
                          <IconRole userRole={iconName} />
                        </div>
                      </Tooltip.Trigger>
                      <Tooltip.Content side="bottom">{builderUserRoleName ?? "super_admin"}</Tooltip.Content>
                    </Tooltip.Root>
                    <span>{userName}</span>
                  </div>
                  <div>{user?.email}</div>
                </div>
                {isSuperAdmin && (
                  <Link href={adminPath({ builderId })}>
                    <IconSalesConfig /> Admin Dashboard
                  </Link>
                )}
                {user?.builderAccounts && user.builderAccounts.length > 1 && (
                  <Link href={accountSwitcherPath()}>
                    <IconUpArrow /> Switch Account
                  </Link>
                )}
                {roleImpersonationEnabled && (
                  <PopoverMenu
                    innerText="Switch Role"
                    menuItems={roleOptions}
                    position="left"
                    disabled={!builderUser}
                    disabledMessage="Role impersonation only available when viewing a builder you belong to."
                    onClose={() => setOpenMenu(false)}
                  />
                )}
                {selectBuilder && (
                  <Link
                    onClick={() => {
                      setShowSelectBuilderModal(true);
                      setOpenMenu(false);
                    }}
                  >
                    Select Builder
                  </Link>
                )}
                {(isSuperAdmin || roleImpersonationEnabled) && <hr />}
                {builderLinksVisible && (
                  <Link href={builderHomePagePath(builderId)}>
                    <IconDashboard /> Dashboard
                  </Link>
                )}
                {studioLinkVisible && (
                  <Link href={studioPath(undefined, isSuperAdmin ? undefined : builderId)}>
                    <IconFileOpen /> Studio
                  </Link>
                )}
                {builderLinksVisible && hasPermission("VIEW_BUILDER_INFO") && (
                  <Link href={builderAccountPath(builderId)}>
                    <IconSettings12 /> Account Settings
                  </Link>
                )}
                {(builderLinksVisible || studioLinkVisible) && <hr />}
                <Link href="" onClick={() => logout(ensureDefined(user?.id))}>
                  <IconSignOut /> Sign Out
                </Link>
              </>
            </DropdownMenu>
          </>
        )}
      </div>
      {showSelectBuilderModal && (
        <SelectBuilderModal onClose={() => setShowSelectBuilderModal(false)} onSelect={selectBuilder} />
      )}
    </>
  );
}
